import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, TextField, Typography } from '@mui/material';
import React, { Component } from 'react';

import { loadExercisesByTags } from '../../api/exercises';
import { ExerciseSearchItem } from '../../api/exercises.interface';
import { ProgramWorkoutItemRecord } from '../../api/programs.interface';
import ProgramImageUploadDropzone from './ProgramImageUploadDropzone';
import ProgramTagExerciseSelectorDialog from './ProgramTagExerciseSelectorDialog';
import { ProgramDayEditorErrors, ProgramDayEditorMode, ProgramDayEditorValues } from './programs.types';
import {
    createExerciseSelectionPlaceholder,
    createProgramDayEditorValues,
    createProgramWorkoutItem,
    parseProgramTagString,
    validateProgramDayEditor,
} from './programs.utils';

interface Props {
    day: number;
    initialItem?: ProgramWorkoutItemRecord;
    mode: ProgramDayEditorMode;
    onCancel: () => void;
    onSave: (item: ProgramWorkoutItemRecord) => void;
    visible: boolean;
    week: number;
}

interface State {
    errors: ProgramDayEditorErrors;
    loadingSelections: boolean;
    primaryExercises: ExerciseSearchItem[];
    secondaryExercises: ExerciseSearchItem[];
    showPrimarySelector: boolean;
    showSecondarySelector: boolean;
    values: ProgramDayEditorValues;
}

class ProgramDayEditorModal extends Component<Props, State> {
    state: State = {
        errors: {},
        loadingSelections: false,
        primaryExercises: [],
        secondaryExercises: [],
        showPrimarySelector: false,
        showSecondarySelector: false,
        values: createProgramDayEditorValues(),
    };

    componentDidUpdate(prevProps: Props) {
        const opened = !prevProps.visible && this.props.visible;
        const changedDay = prevProps.day !== this.props.day || prevProps.week !== this.props.week;
        const changedItem = prevProps.initialItem !== this.props.initialItem;

        if (opened || (this.props.visible && (changedDay || changedItem))) {
            this.hydrateFromProps();
        }
    }

    getMergedSelections(tags: number[], loadedItems: ExerciseSearchItem[]) {
        return tags.map((tag) => loadedItems.find((item) => item.tag === tag) || createExerciseSelectionPlaceholder(tag));
    }

    async hydrateFromProps() {
        const primaryTags = parseProgramTagString(this.props.initialItem?.primaryTag);
        const secondaryTags = parseProgramTagString(this.props.initialItem?.secondaryTag);

        this.setState({
            errors: {},
            loadingSelections: primaryTags.length > 0 || secondaryTags.length > 0,
            primaryExercises: primaryTags.map((tag) => createExerciseSelectionPlaceholder(tag)),
            secondaryExercises: secondaryTags.map((tag) => createExerciseSelectionPlaceholder(tag)),
            showPrimarySelector: false,
            showSecondarySelector: false,
            values: createProgramDayEditorValues(this.props.initialItem),
        });

        if (!primaryTags.length && !secondaryTags.length) {
            this.setState({ loadingSelections: false });
            return;
        }

        const [loadedPrimaryExercises, loadedSecondaryExercises] = await Promise.all([
            loadExercisesByTags(primaryTags),
            loadExercisesByTags(secondaryTags),
        ]);

        if (!this.props.visible) {
            return;
        }

        this.setState({
            loadingSelections: false,
            primaryExercises: this.getMergedSelections(primaryTags, loadedPrimaryExercises),
            secondaryExercises: this.getMergedSelections(secondaryTags, loadedSecondaryExercises),
        });
    }

    onClosePrimarySelector = () => {
        this.setState({ showPrimarySelector: false });
    };

    onCloseSecondarySelector = () => {
        this.setState({ showSecondarySelector: false });
    };

    onFieldChanged = (name: keyof ProgramDayEditorValues, value: string) => {
        this.setState((currentState) => ({
            errors: {
                ...currentState.errors,
                [name]: undefined,
            },
            values: {
                ...currentState.values,
                [name]: value,
            },
        }));
    };

    onOpenPrimarySelector = () => {
        this.setState({ showPrimarySelector: true });
    };

    onOpenSecondarySelector = () => {
        this.setState({ showSecondarySelector: true });
    };

    onProgramImageUploaded = (imageUrl: string) => {
        this.onFieldChanged('imageUrl', imageUrl);
    };

    onPrimarySelectionsConfirmed = (primaryExercises: ExerciseSearchItem[]) => {
        this.setState({ primaryExercises, showPrimarySelector: false });
    };

    onRemovePrimaryExercise = (tag: number) => {
        this.setState((currentState) => ({
            primaryExercises: currentState.primaryExercises.filter((item) => item.tag !== tag),
        }));
    };

    onRemoveSecondaryExercise = (tag: number) => {
        this.setState((currentState) => ({
            secondaryExercises: currentState.secondaryExercises.filter((item) => item.tag !== tag),
        }));
    };

    onSaveClick = () => {
        const validationErrors = validateProgramDayEditor(this.state.values);

        if (Object.keys(validationErrors).length) {
            this.setState({ errors: validationErrors });
            return;
        }

        this.props.onSave(
            createProgramWorkoutItem(
                this.state.values,
                this.props.week,
                this.props.day,
                this.state.primaryExercises,
                this.state.secondaryExercises,
            ),
        );
    };

    onSecondarySelectionsConfirmed = (secondaryExercises: ExerciseSearchItem[]) => {
        this.setState({ secondaryExercises, showSecondarySelector: false });
    };

    render() {
        const { day, mode, onCancel, visible, week } = this.props;
        const { errors, loadingSelections, primaryExercises, secondaryExercises, showPrimarySelector, showSecondarySelector, values } = this.state;

        return (
            <>
                <Drawer anchor="right" open={visible} onClose={onCancel}>
                    <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', height: '100%', width: { md: 520, xs: '100vw' } }}>
                        <Box sx={{ alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', px: 3, py: 2 }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700 }} variant="h6">{mode === 'create' ? 'Add Day' : 'Edit Day'} {day} / Week {week}</Typography>
                                <Typography color="text.secondary" variant="body2">Structured day editing without leaving the builder context.</Typography>
                            </Box>
                            <IconButton onClick={onCancel}>
                                <CloseRoundedIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gap: 2.25, overflowY: 'auto', p: 3 }}>
                            <TextField
                                error={Boolean(errors.description)}
                                fullWidth
                                helperText={errors.description}
                                label="Description"
                                onChange={(event) => this.onFieldChanged('description', event.target.value)}
                                size="small"
                                value={values.description}
                            />
                            <TextField
                                error={Boolean(errors.imageUrl)}
                                fullWidth
                                helperText={errors.imageUrl}
                                label="Image URL"
                                onChange={(event) => this.onFieldChanged('imageUrl', event.target.value)}
                                size="small"
                                value={values.imageUrl}
                            />
                            <ProgramImageUploadDropzone
                                imageUrl={values.imageUrl}
                                onImageUploaded={this.onProgramImageUploaded}
                                title={values.description || `Day ${day} / Week ${week}`}
                            />
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: 'repeat(2, minmax(0, 1fr))', xs: 'minmax(0, 1fr)' } }}>
                                <TextField
                                    error={Boolean(errors.level)}
                                    fullWidth
                                    helperText={errors.level}
                                    label="Level"
                                    onChange={(event) => this.onFieldChanged('level', event.target.value)}
                                    size="small"
                                    value={values.level}
                                />
                                <TextField
                                    error={Boolean(errors.primaryType)}
                                    fullWidth
                                    helperText={errors.primaryType}
                                    label="Primary Type"
                                    onChange={(event) => this.onFieldChanged('primaryType', event.target.value)}
                                    size="small"
                                    value={values.primaryType}
                                />
                                <TextField
                                    fullWidth
                                    label="Secondary Type"
                                    onChange={(event) => this.onFieldChanged('secondaryType', event.target.value)}
                                    size="small"
                                    value={values.secondaryType}
                                />
                                <TextField
                                    error={Boolean(errors.rounds)}
                                    fullWidth
                                    helperText={errors.rounds}
                                    label="Rounds"
                                    onChange={(event) => this.onFieldChanged('rounds', event.target.value)}
                                    size="small"
                                    value={values.rounds}
                                />
                                <TextField
                                    fullWidth
                                    label="Time"
                                    onChange={(event) => this.onFieldChanged('time', event.target.value)}
                                    size="small"
                                    value={values.time}
                                />
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'grid', gap: 1.25 }}>
                                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontWeight: 700 }} variant="subtitle2">Primary Exercise Tags</Typography>
                                    {loadingSelections && <CircularProgress size={18} />}
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {primaryExercises.map((item) => (
                                        <Chip key={item.tag} label={`${item.exercise} (${item.tag})`} onDelete={() => this.onRemovePrimaryExercise(item.tag)} />
                                    ))}
                                    {!primaryExercises.length && <Typography color="text.secondary" variant="body2">No primary tags selected.</Typography>}
                                </Box>
                                <Button onClick={this.onOpenPrimarySelector} size="small" startIcon={<AddRoundedIcon />} variant="outlined">Search Exercises</Button>
                            </Box>
                            <Box sx={{ display: 'grid', gap: 1.25 }}>
                                <Typography sx={{ fontWeight: 700 }} variant="subtitle2">Secondary Exercise Tags</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {secondaryExercises.map((item) => (
                                        <Chip key={item.tag} label={`${item.exercise} (${item.tag})`} onDelete={() => this.onRemoveSecondaryExercise(item.tag)} />
                                    ))}
                                    {!secondaryExercises.length && <Typography color="text.secondary" variant="body2">No secondary tags selected.</Typography>}
                                </Box>
                                <Button onClick={this.onOpenSecondarySelector} size="small" startIcon={<AddRoundedIcon />} variant="outlined">Search Exercises</Button>
                            </Box>
                        </Box>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, justifyContent: 'flex-end', px: 3, py: 2 }}>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button onClick={this.onSaveClick} variant="contained">Save Day</Button>
                        </Box>
                    </Box>
                </Drawer>
                <ProgramTagExerciseSelectorDialog
                    onCancel={this.onClosePrimarySelector}
                    onConfirm={this.onPrimarySelectionsConfirmed}
                    selectedItems={primaryExercises}
                    title="Select Exercises For Primary Tags"
                    visible={showPrimarySelector}
                />
                <ProgramTagExerciseSelectorDialog
                    onCancel={this.onCloseSecondarySelector}
                    onConfirm={this.onSecondarySelectionsConfirmed}
                    selectedItems={secondaryExercises}
                    title="Select Exercises For Secondary Tags"
                    visible={showSecondarySelector}
                />
            </>
        );
    }
}

export default ProgramDayEditorModal;